/**
 * What the hub and its web app share: the shapes that cross the wire, and the pure rules (instance status, tokens, addresses).
 * The hub holds NO customer data: an instance only ever reports metadata (a version and a health word), see `heartbeatSchema`.
 */
import { z } from 'zod';

/* ------------------------------------------------------------------ people */

export const ROLES = ['ADMIN', 'STAFF'] as const;
export type Role = (typeof ROLES)[number];
export const ROLE_LABELS: Record<Role, string> = { ADMIN: 'Administrator', STAFF: 'Staff' };

/**
 * Whose account it is. staff = Voxshift staff and MSP engineers; customer = a customer's own people, who see only the instances they are granted
 * and nothing else in the hub. A customer account can never be an administrator.
 */
export const USER_KINDS = ['staff', 'customer'] as const;
export type UserKind = (typeof USER_KINDS)[number];
export const USER_KIND_LABELS: Record<UserKind, string> = { staff: 'Voxshift staff', customer: 'Customer' };

/** The role a person gets on an instance when they sign in to it with their hub account (the instance applies its own ceiling to it). */
export const GRANT_ROLES = ['SUPER_ADMIN', 'PROJECT_MANAGER', 'ENGINEER', 'CUSTOMER'] as const;
export type GrantRole = (typeof GRANT_ROLES)[number];
export const GRANT_ROLE_LABELS: Record<GrantRole, string> = { SUPER_ADMIN: 'Administrator', PROJECT_MANAGER: 'Project manager', ENGINEER: 'Engineer', CUSTOMER: 'Customer' };
/** What a new grant gets until an administrator chooses: customers start with the least. */
export const defaultGrantRole = (kind: UserKind): GrantRole => (kind === 'customer' ? 'CUSTOMER' : 'ENGINEER');

export const MIN_PASSWORD_LENGTH = 12;
export const passwordSchema = z.string().min(MIN_PASSWORD_LENGTH, `Use at least ${MIN_PASSWORD_LENGTH} characters`).max(200);
export const emailSchema = z.string().trim().toLowerCase().email().max(200);
export const totpCodeSchema = z.string().trim().regex(/^\d{6}$/, 'Enter the 6-digit code');

export const loginSchema = z.object({ email: emailSchema, password: z.string().min(1).max(200) }).strict();
export const codeSchema = z.object({ code: totpCodeSchema }).strict();
export const changePasswordSchema = z.object({ current: z.string().min(1).max(200), next: passwordSchema }).strict();

export const createUserSchema = z
  .object({
    email: emailSchema,
    displayName: z.string().trim().min(1).max(80),
    role: z.enum(ROLES),
    kind: z.enum(USER_KINDS).optional().default('staff'),
    /** The starter password an administrator passes on. Left out when the person is invited by email instead. */
    password: passwordSchema.optional(),
    /** Email an invitation link (needs the hub's mail service) instead of setting a starter password. */
    invite: z.boolean().optional().default(false),
  })
  .strict()
  .refine((v) => v.invite || v.password !== undefined, { message: 'Set a starter password, or invite them by email', path: ['password'] })
  .refine((v) => v.kind === 'staff' || v.role === 'STAFF', { message: 'A customer account cannot be an administrator', path: ['role'] });

/** Someone opens their invitation link and chooses their password; the authenticator set-up follows as for everyone. */
export const acceptInviteSchema = z.object({ token: z.string().regex(/^[0-9a-f]{64}$/, 'This invitation link is not valid'), password: passwordSchema }).strict();
export const updateUserSchema = z
  .object({
    displayName: z.string().trim().min(1).max(80).optional(),
    role: z.enum(ROLES).optional(),
    kind: z.enum(USER_KINDS).optional(),
    status: z.enum(['active', 'disabled']).optional(),
    /** Email an invitation link (replaces any earlier one). For someone whose first sign-in is not complete; needs the mail service. */
    resendInvite: z.literal(true).optional(),
    /** Set a new password for the person (they should change it at first sign-in). */
    password: passwordSchema.optional(),
    /** Make them set up their authenticator app again (a lost phone). */
    resetMfa: z.literal(true).optional(),
  })
  .strict();

/**
 * Which instances (customers) a person may open, and the role they get in each when they sign in with single sign-on. Administrators may open
 * every instance, whatever is assigned; a grant for an administrator only decides the role they get through single sign-on.
 * `instanceIds` (older form) keeps the role of instances already granted and gives new ones the default for the person's kind.
 */
const grantSchema = z.object({ instanceId: z.guid(), role: z.enum(GRANT_ROLES) }).strict();
export const setUserAccessSchema = z.union([
  z.object({ grants: z.array(grantSchema).max(500) }).strict(),
  z.object({ instanceIds: z.array(z.guid()).max(500) }).strict(),
]);
export type SetUserAccessInput = z.infer<typeof setUserAccessSchema>;
export interface UserGrant {
  instanceId: string;
  role: GrantRole;
}

export interface UserView {
  id: string;
  email: string;
  displayName: string;
  role: Role;
  kind: UserKind;
  status: 'active' | 'disabled';
  mfaEnrolled: boolean;
  /** Invited by email and has not chosen a password yet. */
  invitePending: boolean;
  /** Set when an administrator created the account or reset the password: the person must choose their own before doing anything else. */
  mustChangePassword: boolean;
  /** How many instances a staff member is assigned to (administrators open all of them). */
  instanceCount: number;
  lastLoginAt: string | null;
  createdAt: string;
}

/** Where a browser is in signing in: the answer to `GET /api/auth/me`. */
export type AuthState =
  | { state: 'anonymous' }
  | { state: 'mfa'; email: string }
  | { state: 'enrol'; email: string }
  | { state: 'authed'; user: UserView };

/* ---------------------------------------------------------------- addresses */

export type AddressResult = { ok: true; url: string } | { ok: false; error: string };

/** The address an instance is opened at: http(s), a host (and port), nothing after it. Stored as its origin, no trailing slash. */
export function normalizeInstanceUrl(input: string): AddressResult {
  let u: URL;
  try {
    u = new URL(input.trim());
  } catch {
    return { ok: false, error: 'That is not a web address. Start it with https://' };
  }
  if (u.protocol !== 'https:' && u.protocol !== 'http:') return { ok: false, error: 'Use an http:// or https:// address.' };
  if (u.username || u.password) return { ok: false, error: 'Leave the user name and password out of the address.' };
  if (u.search || u.hash) return { ok: false, error: 'Leave out everything after the host name.' };
  if (u.pathname !== '/' && u.pathname !== '') return { ok: false, error: 'Voxshift runs at the root of its address; leave out the path.' };
  return { ok: true, url: u.origin };
}

const instanceUrlSchema = z.string().transform((v, ctx) => {
  const r = normalizeInstanceUrl(v);
  if (!r.ok) {
    ctx.addIssue({ code: 'custom', message: r.error });
    return z.NEVER;
  }
  return r.url;
});

/* ---------------------------------------------------------------- instances */

/**
 * The short name an instance is reached at through the hub: `https://<slug>.hub.voxshift.io`. Lower-case letters, digits and hyphens,
 * 2 to 40 characters, starting and ending with a letter or digit. A few names are kept for the hub itself.
 */
export const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])$/;
export const RESERVED_SLUGS = ['www', 'hub', 'api', 'admin', 'app', 'mail', 'cloud', 'static', 'assets', 'login', 'status'] as const;

export const slugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .refine((s) => SLUG_RE.test(s), 'Use 2 to 40 lower-case letters, digits or hyphens (not at the ends).')
  .refine((s) => !(RESERVED_SLUGS as readonly string[]).includes(s), 'That name is reserved for the hub.');

/** A starting suggestion for a slug from an instance's name: "AT&T Voice Consult" becomes "at-t-voice-consult". */
export function slugFromName(name: string): string {
  const s = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/g, '');
  return s.length >= 2 ? s : '';
}

/** The address people open an instance at through the hub. */
export const remoteUrl = (slug: string, hubDomain: string) => `https://${slug}.${hubDomain}`;

/**
 * Whether the instance can be opened through the hub right now.
 * connected = its tunnel is up; off = the customer has switched remote access off on the instance; offline = it has not connected (or not reported yet).
 */
export type RemoteAccess = 'connected' | 'off' | 'offline';
export const REMOTE_ACCESS_LABELS: Record<RemoteAccess, string> = {
  connected: 'Available',
  off: 'Off (customer)',
  offline: 'Not connected',
};

export const INSTANCE_KINDS = ['hosted', 'customer_premises'] as const;
export type InstanceKind = (typeof INSTANCE_KINDS)[number];
export const INSTANCE_KIND_LABELS: Record<InstanceKind, string> = { hosted: 'Hosted by Voxshift', customer_premises: 'Customer premises' };

const nameSchema = z.string().trim().min(2, 'Give it a name').max(80);

export const createInstanceSchema = z
  .object({
    name: nameSchema,
    customerName: z.string().trim().max(120).optional().default(''),
    kind: z.enum(INSTANCE_KINDS),
    baseUrl: instanceUrlSchema,
    /** The name it is reached at through the hub; suggested from the name when left out. */
    slug: slugSchema.optional(),
    notes: z.string().trim().max(2000).optional().default(''),
    /** May this instance send email through the hub's mail service? On by default; switch it off to stop one instance. */
    mailRelay: z.boolean().optional().default(true),
    /** May people sign in to this instance with their hub account? On by default (the instance has its own switch, which starts off). */
    ssoEnabled: z.boolean().optional().default(true),
  })
  .strict();
export type CreateInstanceInput = z.infer<typeof createInstanceSchema>;

export const updateInstanceSchema = z
  .object({
    name: nameSchema.optional(),
    customerName: z.string().trim().max(120).optional(),
    kind: z.enum(INSTANCE_KINDS).optional(),
    baseUrl: instanceUrlSchema.optional(),
    slug: slugSchema.optional(),
    notes: z.string().trim().max(2000).optional(),
    mailRelay: z.boolean().optional(),
    ssoEnabled: z.boolean().optional(),
    /** Switch an instance off: its heartbeats are refused and staff cannot open it from the hub. */
    enabled: z.boolean().optional(),
  })
  .strict();
export type UpdateInstanceInput = z.infer<typeof updateInstanceSchema>;

/** How long after its last heartbeat an instance still counts as online. Instances report every minute. */
export const ONLINE_WITHIN_SECONDS = 180;

export type InstanceStatus = 'never' | 'online' | 'offline' | 'disabled';
export const INSTANCE_STATUS_LABELS: Record<InstanceStatus, string> = {
  never: 'Not connected yet',
  online: 'Online',
  offline: 'Offline',
  disabled: 'Switched off',
};

export function instanceStatus(i: { enabled: boolean; lastSeenAt: string | Date | null }, now: Date = new Date()): InstanceStatus {
  if (!i.enabled) return 'disabled';
  if (!i.lastSeenAt) return 'never';
  const seen = typeof i.lastSeenAt === 'string' ? Date.parse(i.lastSeenAt) : i.lastSeenAt.getTime();
  return now.getTime() - seen <= ONLINE_WITHIN_SECONDS * 1000 ? 'online' : 'offline';
}

export interface InstanceView {
  id: string;
  name: string;
  customerName: string;
  kind: InstanceKind;
  baseUrl: string;
  /** `<slug>.<hub domain>` is where it opens through the hub. */
  slug: string;
  notes: string;
  enabled: boolean;
  status: InstanceStatus;
  remoteAccess: RemoteAccess;
  /** May it send email through the hub's mail service? */
  mailRelay: boolean;
  /** May people sign in to it with their hub account? (The hub's switch for this instance.) */
  ssoEnabled: boolean;
  /** What the instance last said: hub sign-in is on there (true), off there (false), or it has never said (null: a release without single sign-on). */
  reportedSso: boolean | null;
  /** From the last heartbeat; null until it has reported. */
  version: string | null;
  health: 'ok' | 'degraded' | null;
  lastSeenAt: string | null;
  tokenHint: string;
  createdAt: string;
}

/** Returned once, when an instance is created or its token is rotated. The secret cannot be read again. */
export interface InstanceWithToken {
  instance: InstanceView;
  token: string;
}

/* -------------------------------------------------------------- heartbeats */

/**
 * What an instance tells the hub about itself, once a minute. Deliberately tiny and metadata-only: there is no field that could carry
 * a customer's data, and unknown fields are refused (`strict`), so adding one is a conscious change to this file.
 */
export const heartbeatSchema = z
  .object({
    version: z.string().trim().min(1).max(64),
    health: z.enum(['ok', 'degraded']).default('ok'),
    uptimeSeconds: z.number().int().min(0).max(10 * 365 * 86400).optional(),
    /** Whether the customer has allowed people with a hub account to open this instance through the hub (their switch on the instance). */
    remoteAccess: z.boolean().optional(),
    /** Whether hub sign-in (single sign-on) is switched on at this installation. Left out by releases that do not have it. */
    sso: z.boolean().optional(),
    /**
     * The highest revocation id this installation has applied (docs/SSO.md 5.3). Present means "I understand revocations": the reply then carries
     * `ssoRevocations`. Left out by releases that do not have it, which then get exactly the reply they always did.
     */
    ssoRevocationsAck: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER).optional(),
  })
  .strict();
export type HeartbeatInput = z.infer<typeof heartbeatSchema>;

/**
 * One line of the revocation channel: the hub says "the person with this hub user id (`sub`) lost access; the sessions they started on this
 * installation before `before` are over". `reason` tells the installation what else to do (docs/SSO.md 4.7).
 */
export const REVOCATION_REASONS = ['grant_removed', 'user_disabled', 'sessions_revoked', 'role_changed'] as const;
export type RevocationReasonCode = (typeof REVOCATION_REASONS)[number];
export interface SsoRevocation {
  id: number;
  sub: string;
  before: string;
  reason: RevocationReasonCode;
}

/* ------------------------------------------------------------ single sign-on */

/** Where an instance starts a hub sign-in. The hub's Open button sends the browser here once the person is through the gate. */
export const SSO_LOGIN_PATH = '/api/auth/oidc/login?provider=hub';
/** The two addresses an instance may be sent back to after signing in: exact match only. */
export const oidcRedirectUris = (baseUrl: string, slug: string, hubDomain: string): string[] => [`${baseUrl}/api/auth/oidc/callback`, `${remoteUrl(slug, hubDomain)}/api/auth/oidc/callback`];

/**
 * Whether single sign-on works for an instance right now, as a person sees it: on; off at the installation (its administrator has not switched hub
 * sign-in on); off in the hub (this instance's switch); or not available (the installation has never reported it: an older release).
 */
export type SsoState = 'on' | 'off_instance' | 'off_hub' | 'unavailable';
export const ssoState = (i: { ssoEnabled: boolean; reportedSso: boolean | null }): SsoState =>
  i.reportedSso === null ? 'unavailable' : !i.ssoEnabled ? 'off_hub' : i.reportedSso ? 'on' : 'off_instance';
export const SSO_STATE_LABELS: Record<SsoState, string> = {
  on: 'On',
  off_instance: 'Off at the installation',
  off_hub: 'Off in the hub',
  unavailable: 'Not available on this version',
};

/* --------------------------------------------------------------- how to open */

/** Where the browser can test whether an instance's own address is reachable from where the person is: only an https address can be tested from the hub's https page. */
export const probeUrlFor = (baseUrl: string): string | null => (baseUrl.startsWith('https://') ? `${baseUrl}/version.json` : null);

export type Route = 'direct' | 'remote';
/** What the hub knows when someone clicks Open (GET /api/instances/:id/launch-plan). */
export interface LaunchPlan {
  /** The instance's own address. */
  directUrl: string;
  /** Where the browser can test that address (null unless it is https). */
  directProbeUrl: string | null;
  /** The instance's tunnel is up, and this person may use it (they have a grant). */
  remoteAvailable: boolean;
  /** The browser's public address, as the hub sees it, is the instance's: they are on the same network. */
  sameNetwork: boolean;
  recommended: Route;
  /** The hub-wide switch "Test the installation's own address from the browser first". */
  probeEnabled: boolean;
}
/** direct when they are on the same network; else through the hub when that is possible; else direct (the only way left). */
export const recommendedRoute = (p: Pick<LaunchPlan, 'sameNetwork' | 'remoteAvailable'>): Route => (p.sameNetwork ? 'direct' : p.remoteAvailable ? 'remote' : 'direct');

export type ProbeResult = 'reachable' | 'unreachable';
export type RouteDecision =
  /** The browser should test the instance's own address, then ask again with the result. */
  | { action: 'probe' }
  /** Open this way. `notice`: tell the person afterwards how to open it through the hub instead, in case the page does not load. */
  | { action: 'open'; route: Route; notice: boolean };

/**
 * The one place that decides how Open goes. Same network: its own address, no test (and a hint afterwards if the hub route exists).
 * No tunnel: its own address, the only way. Otherwise test the own address from the browser (if the hub-wide switch is on and the address can be tested):
 * reachable means use it, not reachable means go through the hub. With no test possible, the recommendation stands.
 */
export function chooseRoute(plan: LaunchPlan, probe?: ProbeResult): RouteDecision {
  if (plan.sameNetwork) return { action: 'open', route: 'direct', notice: plan.remoteAvailable };
  if (!plan.remoteAvailable) return { action: 'open', route: 'direct', notice: false };
  if (!plan.probeEnabled || !plan.directProbeUrl) return { action: 'open', route: plan.recommended, notice: false };
  if (probe === undefined) return { action: 'probe' };
  return { action: 'open', route: probe === 'reachable' ? 'direct' : 'remote', notice: false };
}

export const openingSettingsSchema = z.object({ probeDirect: z.boolean() }).strict();
export interface OpeningSettingsView {
  /** Test the installation's own address from the browser before choosing the route. */
  probeDirect: boolean;
}

/** What the Open button answers. `sso` says whether the person will be signed in at the instance without a second sign-in. */
export interface LaunchResult {
  url: string;
  mode: 'remote' | 'direct';
  sso: boolean;
  /** Why the person was NOT signed in with their hub account, when they could have been (they did not ask for a plain open). Null when single sign-on was used or not asked about. */
  ssoSkipped: SsoSkipReason | null;
}

/** The reasons the Open button falls back to the instance's own login page. `no_grant` is the one people trip over: administrators can open every instance but get a role only where they hold a grant. */
export type SsoSkipReason = 'no_grant' | 'instance_off' | 'instance_unavailable' | 'instance_off_in_hub' | 'hub_off';
export const SSO_SKIP_HELP: Record<SsoSkipReason, string> = {
  no_grant: 'You were not signed in automatically because you have no role on this instance. Administrators can open every instance, but single sign-on gives a role only where one was granted (Users > your account > Instances); nothing is implied, so a hub account cannot reach every customer by default.',
  instance_off: 'Hub sign-in is switched off at the installation, so it opened at its own login. Its administrator turns it on under Platform settings > Single sign-on.',
  instance_unavailable: 'This installation has not reported hub sign-in (an older version), so it opened at its own login.',
  instance_off_in_hub: 'Single sign-on is switched off for this instance in the hub (Instances > Edit), so it opened at its own login.',
  hub_off: 'Single sign-on is switched off hub-wide (Settings), so it opened at the instance\'s own login.',
};

/**
 * Things worth a second look in an instance's address, shown as warnings while it is typed (they do not block saving). The address is also the one
 * single sign-on sends people back to (`<address>/api/auth/oidc/callback`), and the instance compares it letter for letter with its own Web address
 * (Administration > Web address): when they differ the hub refuses the sign-in with "the return address does not match".
 */
export function instanceAddressWarnings(input: string, opts: { hubHost: string; saved?: string | null }): string[] {
  const r = normalizeInstanceUrl(input);
  if (!r.ok) return [];
  const u = new URL(r.url);
  const out: string[] = [];
  const host = u.hostname.toLowerCase();
  const hub = opts.hubHost.toLowerCase();
  if (host === hub || host.endsWith(`.${hub}`)) out.push('This is an address on the hub itself. Enter the address the customer opens the installation at, not the one through the hub.');
  if (u.protocol === 'http:' && host !== 'localhost' && host !== '127.0.0.1') out.push('This address uses http://, not https://. Use https:// unless this is a test machine; and the installation\'s own Web address must say http:// too, or the return address will not match.');
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host) || host.startsWith('[') || host === 'localhost') out.push('A bare IP address or localhost only works for people on the same network, and the installation\'s Web address must be exactly this.');
  if (opts.saved && normalizeInstanceUrl(opts.saved).ok && (normalizeInstanceUrl(opts.saved) as { url: string }).url !== r.url) {
    out.push('Changing the address changes where single sign-on sends people back to. Change the installation\'s Web address (Administration > Web address) to match, or hub sign-in will stop working for it.');
  }
  return out;
}

/** The address an instance sends people to after a hub sign-in, as the hub will accept it. */
export const returnAddressOf = (baseUrl: string) => `${baseUrl}/api/auth/oidc/callback`;

export interface SsoKeyView {
  kid: string;
  active: boolean;
  createdAt: string;
  retiredAt: string | null;
}
export interface SsoSettingsView {
  /** The kill switch: off, nobody can sign in to any instance with a hub account. */
  enabled: boolean;
  /** The provider address instances are given. */
  issuer: string;
  discoveryUrl: string;
  keys: SsoKeyView[];
}
export const ssoSettingsSchema = z.object({ enabled: z.boolean() }).strict();

/* ------------------------------------------------------------------ tokens */

const TOKEN_RE = /^vxh_([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})_([0-9a-f]{48})$/;

/** An instance token: `vxh_<instance id>_<secret>`. The hub stores only a hash of the secret. */
export function formatInstanceToken(instanceId: string, secretHex: string): string {
  return `vxh_${instanceId}_${secretHex}`;
}

export function parseInstanceToken(token: string): { instanceId: string; secret: string } | null {
  const m = TOKEN_RE.exec(token.trim());
  return m ? { instanceId: m[1]!, secret: m[2]! } : null;
}

/* ------------------------------------------------------------------- audit */

export interface AuditEntry {
  id: number;
  at: string;
  actorEmail: string | null;
  action: string;
  instanceId: string | null;
  instanceName: string | null;
  detail: Record<string, unknown>;
  ip: string | null;
}

export const AUDIT_ACTION_LABELS: Record<string, string> = {
  'auth.login': 'Signed in',
  'auth.login_failed': 'Failed sign-in',
  'auth.logout': 'Signed out',
  'auth.mfa_enrolled': 'Set up an authenticator app',
  'auth.password_changed': 'Changed their password',
  'instance.created': 'Registered an instance',
  'instance.updated': 'Changed an instance',
  'instance.deleted': 'Removed an instance',
  'instance.token_rotated': 'Rotated an instance token',
  'instance.opened': 'Opened an instance',
  'mail.settings_changed': 'Changed the mail service',
  'mail.test_sent': 'Sent a test email',
  'instance.session_started': 'Entered an instance through the hub',
  'user.access_changed': 'Changed which instances a person can open',
  'user.created': 'Created a user',
  'user.updated': 'Changed a user',
  'user.invited': 'Sent an invitation',
  'user.invite_failed': 'An invitation could not be sent',
  'user.invite_accepted': 'Accepted an invitation',
  'sso.authorize': 'Signed in to an instance (single sign-on)',
  'sso.authorize_refused': 'Single sign-on refused',
  'sso.token_issued': 'Single sign-on completed',
  'sso.token_refused': 'Single sign-on token refused',
  'opening.settings_changed': 'Changed how instances are opened',
  'sso.settings_changed': 'Changed the single sign-on switch',
  'sso.key_rotated': 'Rotated the single sign-on signing key',
  'knowledge.uploaded': 'Added a document to the Knowledge base',
  'knowledge.updated': 'Changed a Knowledge base document',
  'knowledge.replaced': 'Uploaded a new version of a document',
  'knowledge.deleted': 'Deleted a Knowledge base document',
  'knowledge.downloaded': 'Downloaded a Knowledge base document',
  'knowledge.version_downloaded': 'Downloaded an earlier version of a document',
};

/* ---------------------------------------------------------------- mail service */

/**
 * The hub's mail service. Instances connected to the hub can hand it their email; the hub sends it from ITS OWN address using the mail server
 * set here. It relays and does not keep: the log holds the instance, the recipient's domain, the size and the outcome, never a subject, an
 * address or a body.
 */
const mailName = z.string().trim().max(80).regex(/^[^\r\n<>"]*$/, 'Leave out line breaks, quotes and angle brackets');

export const mailSettingsSchema = z
  .object({
    enabled: z.boolean(),
    host: z.string().trim().max(253).default(''),
    port: z.number().int().min(1).max(65535).default(587),
    /** true = encrypted from the start (port 465); false = STARTTLS, which is always required (never plain text). */
    secure: z.boolean().default(false),
    user: z.string().trim().max(254).default(''),
    /** Left out = keep the saved password; an empty string = remove it. */
    password: z.string().max(500).optional(),
    fromAddress: z.string().trim().email().max(254).or(z.literal('')).default(''),
    fromName: mailName.default('Voxshift'),
    /** The most each instance may send. */
    hourlyLimit: z.number().int().min(1).max(100000).default(120),
    dailyLimit: z.number().int().min(1).max(1000000).default(1000),
  })
  .strict()
  .refine((v) => !v.enabled || (v.host && v.fromAddress), { message: 'To switch the mail service on, enter the mail server and the From address', path: ['host'] });
export type MailSettingsInput = z.infer<typeof mailSettingsSchema>;

export const mailTestSchema = z.object({ to: emailSchema }).strict();

export interface MailSettingsView {
  enabled: boolean;
  /** On, with a mail server and a From address: instances can use it. */
  ready: boolean;
  host: string;
  port: number;
  secure: boolean;
  user: string;
  passwordSet: boolean;
  fromAddress: string;
  fromName: string;
  hourlyLimit: number;
  dailyLimit: number;
}

/** What an instance sends the hub's mail service (POST /api/v1/mail). The From address is the hub's, so there is no field for it. */
export const MAX_RELAY_MAIL_CHARS = 1_000_000;
export const relayMailSchema = z
  .object({
    to: emailSchema,
    toName: mailName.nullish(),
    subject: z.string().min(1).max(300).regex(/^[^\r\n]+$/, 'No line breaks in a subject'),
    html: z.string().min(1).max(MAX_RELAY_MAIL_CHARS),
    text: z.string().min(1).max(MAX_RELAY_MAIL_CHARS),
    replyTo: emailSchema.nullish(),
    /** The name shown with the hub's From address, e.g. the customer's own name. */
    fromName: mailName.nullish(),
  })
  .strict();
export type RelayMailInput = z.infer<typeof relayMailSchema>;

export interface MailLogEntry {
  id: number;
  at: string;
  instanceName: string;
  toDomain: string;
  bytes: number;
  status: 'sent' | 'failed' | 'rejected';
  error: string | null;
}

/* --------------------------------------------------------- knowledge base */

/**
 * The Knowledge base: customer-ready documents (guides, manuals, security papers, the demo) that hub people download. Each document is either for
 * everyone with a hub account or for Voxshift staff only; the server decides what a person may list or fetch (a customer account is never staff).
 */
export const KB_CATEGORIES = ['installation', 'administration', 'security', 'product', 'other'] as const;
export type KbCategory = (typeof KB_CATEGORIES)[number];
export const KB_CATEGORY_LABELS: Record<KbCategory, string> = { installation: 'Installation', administration: 'Administration', security: 'Security', product: 'Product', other: 'Other' };

export const KB_VISIBILITIES = ['everyone', 'staff'] as const;
export type KbVisibility = (typeof KB_VISIBILITIES)[number];
export const KB_VISIBILITY_LABELS: Record<KbVisibility, string> = { everyone: 'Everyone with a hub account', staff: 'Voxshift staff only' };

/** The kinds of file that can be uploaded. The server checks the extension AND the first bytes of the file; the browser's content type is never trusted. */
export const KB_FILE_TYPES = ['docx', 'pdf', 'pptx', 'xlsx', 'md', 'zip'] as const;
export type KbFileType = (typeof KB_FILE_TYPES)[number];
export const KB_FILE_TYPE_LABELS: Record<KbFileType, string> = { docx: 'Word', pdf: 'PDF', pptx: 'PowerPoint', xlsx: 'Excel', md: 'Text (Markdown)', zip: 'Zip archive' };
export const KB_MAX_BYTES = 25 * 1024 * 1024;

const kbVersion = z.string().trim().regex(/^[A-Za-z0-9][A-Za-z0-9 ._-]{0,19}$/, 'Use a short version such as 1.0 or 2026.10 (letters, digits, dots and hyphens)');

export const kbMetaSchema = z
  .object({
    title: z.string().trim().min(1, 'Give the document a title').max(120),
    description: z.string().trim().max(400).default(''),
    category: z.enum(KB_CATEGORIES),
    visibility: z.enum(KB_VISIBILITIES),
    version: kbVersion.default('1.0'),
  })
  .strict();
export type KbMetaInput = z.infer<typeof kbMetaSchema>;

export const kbUpdateSchema = z
  .object({
    title: z.string().trim().min(1, 'Give the document a title').max(120).optional(),
    description: z.string().trim().max(400).optional(),
    category: z.enum(KB_CATEGORIES).optional(),
    visibility: z.enum(KB_VISIBILITIES).optional(),
    version: kbVersion.optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to change' });
export type KbUpdateInput = z.infer<typeof kbUpdateSchema>;

export const kbReplaceSchema = z.object({ version: kbVersion }).strict();

export interface KbDocumentView {
  id: string;
  title: string;
  description: string;
  category: KbCategory;
  visibility: KbVisibility;
  version: string;
  fileType: KbFileType;
  sizeBytes: number;
  /** The name the file downloads as (generated; never the name it was uploaded with). */
  downloadName: string;
  updatedAt: string;
  /** Administrators only: */
  versionCount?: number;
  sha256?: string;
  originalName?: string;
  updatedBy?: string | null;
}

export interface KbVersionView {
  id: string;
  version: string;
  fileType: KbFileType;
  sizeBytes: number;
  originalName: string;
  sha256: string;
  uploadedAt: string;
  uploadedBy: string | null;
  current: boolean;
  downloadName: string;
}

/** The file extension of a name, lower case and without the dot ("" when there is none). Only the last part counts: "a.pdf.exe" is "exe". */
export function fileExtension(name: string): string {
  const i = name.lastIndexOf('.');
  return i < 0 ? '' : name.slice(i + 1).toLowerCase();
}

export const isKbFileType = (ext: string): ext is KbFileType => (KB_FILE_TYPES as readonly string[]).includes(ext);

/** The name a document downloads as: made from the title and version only, plain letters, digits and hyphens, so nothing a person typed can reach a header or a path. */
export function kbDownloadName(title: string, version: string, type: string): string {
  const slug = (t: string, max: number) =>
    t.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, max).replace(/-+$/g, '');
  return `${slug(title, 70) || 'document'}-v${slug(version, 20) || '1'}.${isKbFileType(type) ? type : 'bin'}`;
}
